import classNames from "classnames";
import React, { useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  PaymentPlansDocument,
  usePaymentOrderUnsubscribeMutation,
  usePaymentPlansQuery,
} from "../../../graphql/payment.hooks";
import {
  isPaymentError,
  isPaymentLoading,
  selectedCurrency,
} from "../../../graphql/state";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { useApolloClient, useReactiveVar } from "@apollo/client";
import { PAYMENT_STATUS, TransElement, TRIAL_TERM } from "../../../utils";
import {
  findRenewalPlan,
  formatRenewalPrice,
} from "../../../services/subscriptionTrial";
import { useUserQuery } from "../../../graphql/menu.hooks";

type Props = {
  activePremium?: boolean;
  premiumOnly?: boolean;
  trialActive?: boolean;
  trialEndsAt?: string | null;
};

export default function PaidSubscription({
  activePremium = false,
  premiumOnly = false,
  trialActive,
  trialEndsAt,
}: Props) {
  const { t, i18n } = useTranslation();
  const lng = useGetUrlLocale();
  const client = useApolloClient();
  const currentCurrency = useReactiveVar(selectedCurrency);
  const paymentLoading = useReactiveVar(isPaymentLoading);

  const { data: userData } = useUserQuery();
  const { data, error } = usePaymentPlansQuery({
    variables: {
      locale: !lng ? "en" : lng.slice(1),
      currency: currentCurrency,
    },
  });

  const [paymentUnsubscribe, { loading: unsubscribeLoading }] =
    usePaymentOrderUnsubscribeMutation({
      refetchQueries: [
        {
          query: PaymentPlansDocument,
          fetchPolicy: "network-only",
          variables: {
            locale: !lng ? "en" : lng.slice(1),
            currency: currentCurrency,
          },
        },
      ],
    });

  const isTrialActive = premiumOnly
    ? Boolean(trialActive)
    : Boolean(userData?.user?.userPremium?.trialActive);
  const trialEndDate =
    (premiumOnly && trialEndsAt) ||
    userData?.user?.userPremium?.userPremiumEndDate;
  const isPremiumOnlyWithoutPlan =
    premiumOnly && !isTrialActive && !activePremium;
  const hasEndedTrial = isPremiumOnlyWithoutPlan && !!trialEndsAt;
  const hasNeverStartedTrial = isPremiumOnlyWithoutPlan && !trialEndsAt;

  const trialPlan = findRenewalPlan(data?.paymentPlans.plans);
  const renewalPrice = formatRenewalPrice(trialPlan, currentCurrency);

  // Backend may not send a date (or send an invalid one): never render "Invalid Date"
  const formattedDate = (date?: string | null) => {
    if (!date) return null;

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return null;

    return parsedDate.toLocaleDateString(i18n.language, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };
  const trialEndDateLabel = formattedDate(trialEndDate);
  const trialEndsAtLabel = formattedDate(trialEndsAt);
  const subscriptionEndDateLabel = formattedDate(data?.paymentPlans?.endAt);

  const handleUnsubscribe = () => {
    const lastActiveSubscription = data?.paymentPlans?.paymentOrders.find(
      ({ status }) => status === 1
    );
    if (!lastActiveSubscription?.id) return;

    paymentUnsubscribe({
      variables: {
        orderId: lastActiveSubscription?.id,
      },
    });

    setTimeout(() => {
      client.query({
        query: PaymentPlansDocument,
        fetchPolicy: "network-only",
        variables: {
          locale: !lng ? "en" : lng.slice(1),
          currency: currentCurrency,
        },
      });
    }, 3000);
  };

  useEffect(() => {
    if (unsubscribeLoading) {
      isPaymentLoading(true);
    }
  }, [unsubscribeLoading]);

  useEffect(() => {
    if (!error) return;
    isPaymentError(true);
  }, [error]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-2">
          <div className="fw-bold fs-2">{t("payment.Premium")}</div>
          {isTrialActive && (
            <div className="py-1 px-2 bg-primary-light text-primary rounded-2 fs-7">
              {t("payment.Free Trial")}
            </div>
          )}
          {hasEndedTrial && (
            <div className="py-1 px-2 bg-additional-gray text-gray rounded-2 fs-7">
              {t("payment.Trial Has Ended")}
            </div>
          )}
        </div>
      </div>
      <hr />
      <div className="mt-4">
        {isTrialActive ? (
          <span className="text-gray">
            {trialEndDateLabel ? (
              <TransElement
                i18nKey="payment.trialPlanData"
                values={{
                  date: trialEndDateLabel,
                  price: renewalPrice ?? "",
                  plan: t("payment.year"),
                }}
                components={{ b: <span className="fw-bold" /> }}
              />
            ) : (
              `${t("modal.activeTrialAccessUntil")} ${t("modal.activeTrialFallbackDate")}.`
            )}
          </span>
        ) : hasEndedTrial && trialEndsAtLabel ? (
          <span className="text-gray">
            {t("payment.Premium trial ended on", {
              days: trialPlan?.trialDays ?? TRIAL_TERM,
              date: trialEndsAtLabel,
            })}
          </span>
        ) : hasEndedTrial || hasNeverStartedTrial ? (
          <span className="text-gray">
            {t("payment.Choose a Premium plan to start using ME-Page.")}
          </span>
        ) : (
          <>
            <span className="text-gray">
              {data?.paymentPlans?.paymentOrders[0]?.status ===
              PAYMENT_STATUS.CANCELED
                ? t("payment.Your subscription is canceled and will expire on")
                : t("payment.Your subscription is active until on")}
            </span>{" "}
            {subscriptionEndDateLabel}
          </>
        )}
      </div>
      {data?.paymentPlans?.paymentOrders[0]?.status ===
        PAYMENT_STATUS.COMPLETED && (
        <Button
          onClick={handleUnsubscribe}
          variant="link"
          className={classNames("p-0 text-dark fs-6", {
            disabled: paymentLoading,
          })}
        >
          {isTrialActive
            ? t("payment.Cancel Trial")
            : t("payment.Cancel Subscription")}
          {paymentLoading && (
            <Spinner
              animation="border"
              role="status"
              size="sm"
              className="ms-2"
            />
          )}
        </Button>
      )}
    </>
  );
}
